import { createSlice } from "@reduxjs/toolkit";
import { Dispatch } from "redux";
import axios from "../utils/axios.tsx";
import {
  ConversationState,
  Exchange,
  ExchangeState,
  ConversationGroups,
  Dashboard,
  DashboardTile,
  DashboardTileLayout,
  RootState,
} from "./types.tsx";

function isLaterThanNumberOfDaysInThePast(
  date: Date,
  numberOfDaysInThePast: number,
) {
  var cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - numberOfDaysInThePast);
  return cutoffDate < date;
}

function isEarlierThanNumberOfDaysInThePast(
  date: Date,
  numberOfDaysInThePast: number,
) {
  var cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - numberOfDaysInThePast);
  return cutoffDate >= date;
}

const initialState: ConversationState = {
  exchangesByConversation: {},
  conversations: {
    pinned: [],
    last_7_days: [],
    last_30_days: [],
    other: [],
  },
  selectedConversation: "",
  selectedView: "conversation",
  selectedDashboard: "",
  dashboardsById: {},
  dashboardIds: [],
  dashboardTileIdsByDashboard: {},
  dashboardTileTypesById: {},
  dashboardTileLayoutsById: {},
  dashboardTilesById: {},
  dashboardLoadingById: {},
  dashboardStatusByExchangeId: {},
  availableModels: [],
  selectedModel: "",
  userStoppedStream: false,
};

const setDashboardTileLayout = (
  state: ConversationState,
  tileId: string,
  layout?: DashboardTile["layout"] | null,
) => {
  const normalizedLayout = layout || null;
  state.dashboardTileLayoutsById[tileId] = normalizedLayout;

  const tile = state.dashboardTilesById[tileId];
  if (tile) {
    tile.layout = normalizedLayout;
  }
};

const toFetchedDashboardTileLayoutMap = (
  items: Array<{
    id: string;
    layout?: DashboardTile["layout"] | null;
  }>,
): Record<string, DashboardTileLayout | null> =>
  items.reduce(
    (accumulator, item) => {
      accumulator[item.id] = item.layout || null;
      return accumulator;
    },
    {} as Record<string, DashboardTileLayout | null>,
  );

const toSavedDashboardTileLayoutMap = (
  layouts: Array<
    {
      id: string;
    } & NonNullable<DashboardTile["layout"]>
  >,
): Record<string, DashboardTileLayout | null> =>
  layouts.reduce(
    (accumulator, layout) => {
      accumulator[layout.id] = {
        column: layout.column,
        row: layout.row,
        column_span: layout.column_span,
        row_span: layout.row_span,
      };
      return accumulator;
    },
    {} as Record<string, DashboardTileLayout | null>,
  );

const applyDashboardTileResponse = (
  dispatch: Dispatch,
  tile: DashboardTile,
) => {
  dispatch(
    conversationActions.upsertDashboardTile({
      ...tile,
      json_response: {
        content: tile.data,
        type: tile.type,
      },
    }),
  );
  dispatch(
    conversationActions.setDashboardExchangeStatus({
      exchangeId: tile.source_exchange_id,
      status: {
        saved: true,
        loading: false,
        tile_id: tile.id,
        dashboard_id: tile.dashboard_id,
      },
    }),
  );
};

function parseDate(str: string): Date {
  return new Date(str.split(".")[0]);
}

function generateConversations(action: {
  payload: any;
  type: string;
}): ConversationGroups {
  var conversationGroups: ConversationGroups = {
    last_30_days: [],
    last_7_days: [],
    other: [],
    pinned: [],
  };
  for (var item of action.payload) {
    if (item.is_pinned) {
      conversationGroups.pinned.push({
        id: item.id,
        title: item.title,
        summary: item.summary,
        is_pinned: item.is_pinned,
        created_at: item.created_at,
      });
    } else if (
      isLaterThanNumberOfDaysInThePast(parseDate(item.created_at), 7)
    ) {
      conversationGroups.last_7_days.push({
        id: item.id,
        title: item.title,
        summary: item.summary,
        is_pinned: item.is_pinned,
        created_at: item.created_at,
      });
    } else if (
      isEarlierThanNumberOfDaysInThePast(parseDate(item.created_at), 7) &&
      isLaterThanNumberOfDaysInThePast(parseDate(item.created_at), 30)
    ) {
      conversationGroups.last_30_days.push({
        id: item.id,
        title: item.title,
        summary: item.summary,
        is_pinned: item.is_pinned,
        created_at: item.created_at,
      });
    } else {
      conversationGroups.other.push({
        id: item.id,
        title: item.title,
        summary: item.summary,
        is_pinned: item.is_pinned,
        created_at: item.created_at,
      });
    }
  }

  return conversationGroups;
}

export const getSelectedConversationExchanges = (
  state: RootState,
): ExchangeState | undefined => {
  const selectedId = state.conversationState.selectedConversation;
  return state.conversationState.exchangesByConversation[selectedId];
};

export const getSelectedDashboardTileIds = (state: RootState): string[] => {
  const selectedId = state.conversationState.selectedDashboard;
  return state.conversationState.dashboardTileIdsByDashboard[selectedId] || [];
};

export const getSelectedDashboard = (
  state: RootState,
): Dashboard | undefined => {
  const selectedId = state.conversationState.selectedDashboard;
  return state.conversationState.dashboardsById[selectedId];
};

const conversationSlice = createSlice({
  name: "conversation",
  initialState,
  reducers: {
    setSelectedConversation(state, action) {
      let conversationId = action.payload;
      state.selectedConversation = conversationId;
      state.selectedView = "conversation";
      if (!state.exchangesByConversation[conversationId]) {
        state.exchangesByConversation[conversationId] = {
          byId: {},
          allIds: [],
        };
      }
    },
    setSelectedDashboard(state, action) {
      const dashboardId = action.payload;
      state.selectedDashboard = dashboardId;
      state.selectedView = "dashboard";
      if (!state.dashboardTileIdsByDashboard[dashboardId]) {
        state.dashboardTileIdsByDashboard[dashboardId] = [];
      }
    },
    setSelectedView(state, action) {
      state.selectedView = action.payload;
    },
    setDashboardLoading(state, action) {
      const { dashboardId, isLoading } = action.payload;
      state.dashboardLoadingById[dashboardId] = isLoading;
    },
    setDashboardExchangeStatus(state, action) {
      const { exchangeId, status } = action.payload;
      state.dashboardStatusByExchangeId[exchangeId] = {
        ...state.dashboardStatusByExchangeId[exchangeId],
        ...status,
      };
    },
    resetSelectedConversation(state) {
      state.selectedConversation = "";
    },
    loadDashboards(state, action) {
      state.dashboardIds = [];
      state.dashboardsById = {};
      state.dashboardLoadingById = {};

      action.payload.forEach((dashboard: Dashboard) => {
        state.dashboardIds.push(dashboard.id);
        state.dashboardsById[dashboard.id] = dashboard;
      });
    },
    upsertDashboard(state, action) {
      const dashboard: Dashboard = action.payload;
      state.dashboardsById[dashboard.id] = dashboard;
      if (!state.dashboardIds.includes(dashboard.id)) {
        state.dashboardIds.push(dashboard.id);
      }
      state.dashboardIds.sort((a, b) => {
        const aDate = new Date(
          state.dashboardsById[a].last_updated_at,
        ).getTime();
        const bDate = new Date(
          state.dashboardsById[b].last_updated_at,
        ).getTime();
        return bDate - aDate;
      });
    },
    removeDashboard(state, action) {
      const dashboardId = action.payload;
      const removedTileIds = new Set(
        state.dashboardTileIdsByDashboard[dashboardId] || [],
      );
      delete state.dashboardsById[dashboardId];
      delete state.dashboardTileIdsByDashboard[dashboardId];
      delete state.dashboardLoadingById[dashboardId];
      state.dashboardIds = state.dashboardIds.filter(
        (id) => id !== dashboardId,
      );

      const remainingTiles: Record<string, DashboardTile> = {};
      Object.entries(state.dashboardTilesById).forEach(([tileId, tile]) => {
        if (tile.dashboard_id !== dashboardId) {
          remainingTiles[tileId] = tile;
        }
      });
      state.dashboardTilesById = remainingTiles;
      const remainingTileTypes: Record<string, string> = {};
      Object.entries(state.dashboardTileTypesById).forEach(([tileId, type]) => {
        if (!removedTileIds.has(tileId)) {
          remainingTileTypes[tileId] = type;
        }
      });
      state.dashboardTileTypesById = remainingTileTypes;
      const remainingTileLayouts: Record<string, DashboardTileLayout | null> =
        {};
      Object.entries(state.dashboardTileLayoutsById).forEach(
        ([tileId, layout]) => {
          if (!removedTileIds.has(tileId)) {
            remainingTileLayouts[tileId] = layout || null;
          }
        },
      );
      state.dashboardTileLayoutsById = remainingTileLayouts;
      Object.entries(state.dashboardStatusByExchangeId).forEach(
        ([exchangeId, status]) => {
          if (status.dashboard_id === dashboardId) {
            state.dashboardStatusByExchangeId[exchangeId] = {
              saved: false,
              loading: false,
            };
          }
        },
      );

      if (state.selectedDashboard === dashboardId) {
        state.selectedDashboard = "";
        state.selectedView = "conversation";
      }
    },
    loadDashboardTileIds(state, action) {
      const { dashboardId, tileIds, tileTypes, tileLayouts } =
        action.payload as {
          dashboardId: string;
          tileIds: string[];
          tileTypes: Record<string, string>;
          tileLayouts: Record<string, DashboardTile["layout"]>;
        };
      state.dashboardTileIdsByDashboard[dashboardId] = tileIds;
      Object.entries(tileTypes).forEach(([tileId, type]) => {
        state.dashboardTileTypesById[tileId] = type;
      });
      Object.entries(tileLayouts).forEach(([tileId, layout]) => {
        setDashboardTileLayout(state, tileId, layout);
      });
    },
    appendDashboardTileSummary(state, action) {
      const { dashboardId, tileId, type, layout } = action.payload;
      if (!state.dashboardTileIdsByDashboard[dashboardId]) {
        state.dashboardTileIdsByDashboard[dashboardId] = [];
      }
      if (!state.dashboardTileIdsByDashboard[dashboardId].includes(tileId)) {
        state.dashboardTileIdsByDashboard[dashboardId].push(tileId);
      }
      if (type) {
        state.dashboardTileTypesById[tileId] = type;
      }
      setDashboardTileLayout(state, tileId, layout);
    },
    upsertDashboardTile(state, action) {
      const tile: DashboardTile = action.payload;
      state.dashboardTilesById[tile.id] = tile;
      setDashboardTileLayout(state, tile.id, tile.layout);
      if (!state.dashboardTileIdsByDashboard[tile.dashboard_id]) {
        state.dashboardTileIdsByDashboard[tile.dashboard_id] = [];
      }
      if (
        !state.dashboardTileIdsByDashboard[tile.dashboard_id].includes(tile.id)
      ) {
        state.dashboardTileIdsByDashboard[tile.dashboard_id].push(tile.id);
      }
    },
    invalidateDashboardTile(state, action) {
      const tileId = action.payload as string;
      delete state.dashboardTilesById[tileId];
    },
    removeDashboardTile(state, action) {
      const { tileId, dashboardId } = action.payload;
      state.dashboardTileIdsByDashboard[dashboardId] = (
        state.dashboardTileIdsByDashboard[dashboardId] || []
      ).filter((id) => id !== tileId);
      delete state.dashboardTilesById[tileId];
      delete state.dashboardTileTypesById[tileId];
      delete state.dashboardTileLayoutsById[tileId];
    },
    updateDashboardTileLayouts(state, action) {
      const { layouts } = action.payload as {
        layouts: Record<string, DashboardTile["layout"]>;
      };
      Object.entries(layouts).forEach(([tileId, layout]) => {
        setDashboardTileLayout(state, tileId, layout);
      });
    },
    loadAllExchanges(state, action) {
      const conversationId = action.payload.conversationId;

      // Ensure the conversation entry exists
      if (!state.exchangesByConversation[conversationId]) {
        state.exchangesByConversation[conversationId] = {
          byId: {},
          allIds: [],
        };
      }

      const current = state.exchangesByConversation[conversationId];

      action.payload.exchanges.forEach((exchange: Exchange) => {
        current.byId[exchange.id] = {
          ...current.byId[exchange.id],
          ...exchange,
        };
        state.dashboardStatusByExchangeId[exchange.id] = {
          saved: Boolean(exchange.dashboard_saved),
          loading: false,
          tile_id: exchange.dashboard_tile_id || undefined,
          dashboard_title: exchange.dashboard_title || undefined,
        };
        if (!current.allIds.includes(exchange.id)) {
          current.allIds.push(exchange.id);
        }
      });
    },
    resetExchanges(state) {
      if (state.selectedConversation) {
        state.exchangesByConversation[state.selectedConversation] = {
          byId: {},
          allIds: [],
        };
      }
    },
    appendExchange(state, action) {
      const conversationId =
        action.payload.conversation_id || state.selectedConversation;

      if (
        action.payload.conversation_id &&
        state.exchangesByConversation["new"] &&
        state.selectedConversation == "new"
      ) {
        state.exchangesByConversation[conversationId] =
          state.exchangesByConversation["new"];
        state.selectedConversation = conversationId;

        delete state.exchangesByConversation["new"];
      }
      if (!state.exchangesByConversation[conversationId]) {
        state.exchangesByConversation[conversationId] = {
          byId: {},
          allIds: [],
        };
      }

      if (action.payload.response === null) {
        const newId =
          action.payload.id !== undefined
            ? action.payload.id
            : `last-exchange-temp-${state.exchangesByConversation[conversationId].allIds.length}`;
        state.exchangesByConversation[conversationId].allIds.push(newId);
        state.exchangesByConversation[conversationId].byId[newId] = {
          id: newId,
          conversation_id: conversationId,
          prompt: action.payload.prompt,
          response: "",
          json_response: null,
          feedback: null,
          user_comment: null,
          progress_update: false,
        };
      } else {
        const exchanges = state.exchangesByConversation[conversationId];
        const lastExchangeId = exchanges.allIds[exchanges.allIds.length - 1];
        let lastExchange = exchanges.byId[lastExchangeId];
        lastExchange.progress_update = action.payload.progress_update;
        lastExchange.response = action.payload.response;
        lastExchange.type = action.payload.type;
        lastExchange.json_response = {
          content: action.payload.response,
          type: action.payload.type,
        };
        lastExchange.feedback = action.payload.feedback;
        lastExchange.user_comment = action.payload.user_comment;
        if (
          String(lastExchangeId).startsWith("last-exchange-temp") &&
          action.payload.id
        ) {
          exchanges.byId[action.payload.id] = exchanges.byId[lastExchangeId];
          delete exchanges.byId[lastExchangeId];
          exchanges.allIds[exchanges.allIds.length - 1] = action.payload.id;
        }
      }
    },
    appendCustomActionResponse(state, action) {
      /*
      Used for custom actions such as Save Memories.
      As these are not true question/response exchanges and don't have a unique id
      originating from the DB, we set the negative value of the next exchange cast as string.
      If exchanges.length == 10, the new id will be "-11"
      */
      const conversationId =
        action.payload.conversation_id || state.selectedConversation;
      if (!conversationId) return;

      if (!state.exchangesByConversation[conversationId]) {
        state.exchangesByConversation[conversationId] = {
          byId: {},
          allIds: [],
        };
      }

      const exchanges = state.exchangesByConversation[conversationId];
      const lastExchangeId = exchanges.allIds[exchanges.allIds.length - 1];
      const lastExchange = lastExchangeId
        ? exchanges.byId[lastExchangeId]
        : undefined;

      // If the last exchange is a custom prompt with no response yet, update it in place
      if (
        lastExchange &&
        typeof lastExchange.response === "string" &&
        lastExchange.response.trim() === "" &&
        typeof lastExchange.prompt === "string" &&
        lastExchange.prompt.trim() !== ""
      ) {
        lastExchange.response = action.payload.text;
        lastExchange.type = action.payload.type;
        lastExchange.json_response = {
          content: action.payload.text,
          type: action.payload.type,
          isStreaming: action.payload.isStreaming || false,
        };
      } else {
        const newId = String(-(exchanges.allIds.length - 1));
        exchanges.allIds.push(newId);
        exchanges.byId[newId] = {
          id: newId,
          conversation_id: conversationId,
          prompt: action.payload.savePrompt ? action.payload.prompt : "",
          response: action.payload.text,
          json_response: {
            content: action.payload.text,
            type: action.payload.type,
            isStreaming: action.payload.isStreaming || false,
          },
        };
      }
    },
    appendCustomActionPrompt(state, action) {
      /*
      As these are not true question/response prompts and don't have a unique id
      originating from the DB, we set the negative value of the next exchange cast as string.
      If exchanges.length == 10, the new id will be "-11"
      */
      const conversationId =
        action.payload.conversation_id || state.selectedConversation;
      if (!conversationId) return;

      if (!state.exchangesByConversation[conversationId]) {
        state.exchangesByConversation[conversationId] = {
          byId: {},
          allIds: [],
        };
      }

      const exchanges = state.exchangesByConversation[conversationId];
      const newId = String(-(exchanges.allIds.length - 1));
      exchanges.allIds.push(newId);
      exchanges.byId[newId] = {
        id: newId,
        conversation_id: conversationId,
        prompt: action.payload.text,
        response: "",
        json_response: {
          content: "",
          type: "",
        },
      };
    },
    updateExchangeById: (state, action) => {
      const { id, updateFields, conversationId } = action.payload;
      if (!state.exchangesByConversation[conversationId]) {
        state.exchangesByConversation[conversationId] = {
          byId: {},
          allIds: [],
        };
      }

      const exchanges = state.exchangesByConversation[conversationId];
      const existing = exchanges.byId[id];

      exchanges.byId[id] = {
        ...(existing || {
          id,
          conversation_id: conversationId,
          prompt: "",
          response: "",
          json_response: null,
          feedback: null,
          user_comment: null,
          progress_update: false,
        }),
        ...updateFields,
      };

      if (!exchanges.allIds.includes(id)) {
        exchanges.allIds.push(id);
      }
    },
    resolveTemporaryExchangeById: (state, action) => {
      const {
        temporaryId,
        temporaryConversationId,
        backendConversationId,
        backendId,
        updateFields,
      } = action.payload;

      if (!state.exchangesByConversation[temporaryConversationId]) {
        state.exchangesByConversation[temporaryConversationId] = {
          byId: {},
          allIds: [],
        };
      }
      if (!state.exchangesByConversation[backendConversationId]) {
        state.exchangesByConversation[backendConversationId] = {
          byId: {},
          allIds: [],
        };
      }

      const sourceExchanges =
        state.exchangesByConversation[temporaryConversationId];
      const targetExchanges =
        state.exchangesByConversation[backendConversationId];
      const sameConversation =
        temporaryConversationId === backendConversationId;

      const temporaryExchange = sourceExchanges.byId[temporaryId];
      const existingBackendExchange = targetExchanges.byId[backendId];
      const baseExchange = temporaryExchange ||
        existingBackendExchange || {
          id: backendId,
          conversation_id: backendConversationId,
          prompt: "",
          response: "",
          json_response: null,
          feedback: null,
          user_comment: null,
          progress_update: false,
        };

      targetExchanges.byId[backendId] = {
        ...baseExchange,
        ...updateFields,
        id: backendId,
        conversation_id: backendConversationId,
      };

      if (sameConversation) {
        if (temporaryId !== backendId && targetExchanges.byId[temporaryId]) {
          delete targetExchanges.byId[temporaryId];
        }
        const tempIndex = targetExchanges.allIds.indexOf(temporaryId);
        if (tempIndex >= 0) {
          targetExchanges.allIds[tempIndex] = backendId;
        } else if (!targetExchanges.allIds.includes(backendId)) {
          targetExchanges.allIds.push(backendId);
        }
      } else {
        if (sourceExchanges.byId[temporaryId]) {
          delete sourceExchanges.byId[temporaryId];
          sourceExchanges.allIds = sourceExchanges.allIds.filter(
            (id: string) => id !== temporaryId,
          );
        }
        if (!targetExchanges.allIds.includes(backendId)) {
          targetExchanges.allIds.push(backendId);
        }
      }

      // Keep order deterministic if backend ID already existed elsewhere.
      targetExchanges.allIds = targetExchanges.allIds.filter(
        (id: string, index: number, arr: string[]) => arr.indexOf(id) === index,
      );
    },
    loadAllConversations(state, action) {
      state.conversations = generateConversations(action);
    },
    setSelectedModel(state, action) {
      state.selectedModel = action.payload;
    },
    setAvailableModels(state, action) {
      const { availableModels, defaultModel } = action.payload;
      state.availableModels = availableModels;
      state.selectedModel = defaultModel;
    },
    setUserStoppedStream(state, action) {
      state.userStoppedStream = action.payload;
    },
  },
});

export const conversationActions = conversationSlice.actions;

export const appendCustomTextResponse = (text: string) => {
  return conversationActions.appendCustomActionResponse({
    text: text,
    type: "TextResponse",
  });
};

export const appendStreamingTextResponse = (
  text: string,
  isStreaming: boolean = false,
) => {
  return conversationActions.appendCustomActionResponse({
    text: text,
    type: "StreamingTextResponse",
    isStreaming: isStreaming,
  });
};

export const appendSignUpOptionsResponse = (
  text: string = "",
  isStreaming: boolean = true,
) => {
  return conversationActions.appendCustomActionResponse({
    text: text,
    type: "SignUpOptions",
    isStreaming: isStreaming,
  });
};

export const fetchConversations = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get("/v1/conversation/", {
        withCredentials: true,
      });

      if (response.status !== 200) {
        console.log("Error fetching conversations");
      } else {
        dispatch(
          conversationActions.loadAllConversations(
            response.data.objects as ConversationGroups,
          ),
        );
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };
};

export const fetchDashboards = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get("/v1/dashboard/", {
        withCredentials: true,
      });

      if (response.status !== 200) {
        console.log("Error fetching dashboards");
      } else {
        dispatch(
          conversationActions.loadDashboards(
            response.data.objects as Dashboard[],
          ),
        );
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching dashboards:", error);
    }
  };
};

export const deleteConversation = (id: string) => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.patch(`/v1/conversation/delete/${id}/`, {
        withCredentials: true,
      });

      if (response.status !== 200) {
        console.log("Error deleting conversation");
      } else {
        dispatch(fetchConversations() as any);
        dispatch(conversationActions.resetSelectedConversation());
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error deleting conversation:", error);
    }
  };
};

export const patchConversation = (id: string, body: string) => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.patch(`/v1/conversation/${id}/`, body, {
        withCredentials: true,
      });

      if (response.status !== 200) {
        console.log("Error patching conversation:", response);
      } else {
        dispatch(fetchConversations() as any);
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error patching conversation:", error);
    }
  };
};

export const addConversation = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.post("/v1/conversation/", {
        withCredentials: true,
      });

      if (response.status !== 201) {
        console.log("Error creating conversation:", response);
      } else {
        dispatch(fetchConversations() as any);
        dispatch(conversationActions.resetExchanges());
        dispatch(conversationActions.resetSelectedConversation());
        dispatch(conversationActions.setSelectedConversation(response.data.id));
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error creating conversation:", error);
    }
  };
};

export const selectConversationAction = (conversation_id: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(conversationActions.setSelectedConversation(conversation_id));
  };
};

export const fetchDashboardTile = (tileId: string) => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get(`/v1/dashboard/tile/${tileId}/`, {
        withCredentials: true,
      });

      if (response.status !== 200) {
        console.log("Error fetching dashboard tile");
      } else {
        const tile = response.data as DashboardTile;
        applyDashboardTileResponse(dispatch, tile);
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching dashboard tile:", error);
    }
  };
};

export const refreshDashboardTile = (tileId: string) => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const existingTile =
      getState().conversationState.dashboardTilesById[tileId];
    if (existingTile) {
      dispatch(
        conversationActions.setDashboardExchangeStatus({
          exchangeId: existingTile.source_exchange_id,
          status: {
            saved: true,
            loading: true,
            tile_id: existingTile.id,
            dashboard_id: existingTile.dashboard_id,
          },
        }),
      );
    }
    dispatch(conversationActions.invalidateDashboardTile(tileId));

    try {
      const response = await axios.patch(
        `/v1/dashboard/tile/${tileId}/refresh/`,
        {},
        {
          withCredentials: true,
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to refresh dashboard tile");
      }

      const tile = response.data as DashboardTile;
      applyDashboardTileResponse(dispatch, tile);

      return tile;
    } catch (error) {
      if (existingTile) {
        dispatch(conversationActions.upsertDashboardTile(existingTile));
        dispatch(
          conversationActions.setDashboardExchangeStatus({
            exchangeId: existingTile.source_exchange_id,
            status: {
              saved: true,
              loading: false,
              tile_id: existingTile.id,
              dashboard_id: existingTile.dashboard_id,
            },
          }),
        );
      }
      throw error;
    }
  };
};

export const fetchDashboardTileIds = (dashboardId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(
      conversationActions.setDashboardLoading({
        dashboardId,
        isLoading: true,
      }),
    );

    const sendRequest = async () => {
      const response = await axios.get(`/v1/dashboard/${dashboardId}/tiles/`, {
        withCredentials: true,
      });

      if (response.status !== 200) {
        console.log("Error fetching dashboard tile ids");
      } else {
        const tiles = response.data.objects as Array<{
          id: string;
          type: string;
          layout?: DashboardTile["layout"];
        }>;
        const tileIds = tiles.map((tile) => tile.id);
        const tileTypes = tiles.reduce(
          (accumulator, tile) => {
            accumulator[tile.id] = tile.type;
            return accumulator;
          },
          {} as Record<string, string>,
        );
        const tileLayouts = toFetchedDashboardTileLayoutMap(tiles);
        dispatch(
          conversationActions.loadDashboardTileIds({
            dashboardId,
            tileIds,
            tileTypes,
            tileLayouts,
          }),
        );
        await Promise.all(
          tileIds.map(
            (tileId) =>
              dispatch(fetchDashboardTile(tileId) as any) as Promise<void>,
          ),
        );
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching dashboard tile ids:", error);
    } finally {
      dispatch(
        conversationActions.setDashboardLoading({
          dashboardId,
          isLoading: false,
        }),
      );
    }
  };
};

export const selectDashboardAction = (dashboardId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(conversationActions.setSelectedDashboard(dashboardId));
    await (dispatch(
      fetchDashboardTileIds(dashboardId) as any,
    ) as Promise<void>);
  };
};

export const saveExchangeToDashboard = ({
  exchangeId,
  dashboardId,
  dashboardTitle,
}: {
  exchangeId: string;
  dashboardId?: string;
  dashboardTitle?: string;
}) => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const requestBody: Record<string, string | number> = {
      exchange_id: exchangeId,
    };
    if (dashboardId) {
      requestBody.dashboard_id = Number(dashboardId);
    }
    if (dashboardTitle) {
      requestBody.dashboard_title = dashboardTitle;
    }

    const response = await axios.post("/v1/dashboard/", requestBody, {
      withCredentials: true,
    });

    if (![200, 201].includes(response.status)) {
      throw new Error("Failed to save dashboard tile");
    }

    const dashboard = response.data.dashboard as Dashboard;
    dispatch(conversationActions.upsertDashboard(dashboard));
    dispatch(
      conversationActions.setDashboardExchangeStatus({
        exchangeId,
        status: {
          saved: true,
          loading: false,
          tile_id: response.data.tile.id,
          dashboard_id: dashboard.id,
          dashboard_title: dashboard.title,
        },
      }),
    );

    const selectedDashboardId = getState().conversationState.selectedDashboard;
    if (selectedDashboardId === dashboard.id) {
      const savedTile = response.data.tile as {
        id: string;
        dashboard_id: string;
        source_exchange_id: string;
        type: string;
        created_at: string;
        last_updated_at: string;
        layout?: DashboardTile["layout"];
      };
      const existingTile =
        getState().conversationState.dashboardTilesById[savedTile.id];

      dispatch(
        conversationActions.appendDashboardTileSummary({
          dashboardId: dashboard.id,
          tileId: savedTile.id,
          type: savedTile.type,
          layout: savedTile.layout,
        }),
      );

      if (!existingTile) {
        void dispatch(fetchDashboardTile(savedTile.id) as any);
      }
    }

    return response.data as {
      dashboard: Dashboard;
      tile: {
        id: string;
        dashboard_id: string;
        source_exchange_id: string;
        type: string;
        created_at: string;
        last_updated_at: string;
        layout?: DashboardTile["layout"];
      };
    };
  };
};

export const saveDashboardTileLayouts = ({
  dashboardId,
  layouts,
}: {
  dashboardId: string;
  layouts: Array<
    {
      id: string;
    } & NonNullable<DashboardTile["layout"]>
  >;
}) => {
  return async (dispatch: Dispatch) => {
    const response = await axios.patch(
      `/v1/dashboard/${dashboardId}/layout/`,
      {
        layouts,
      },
      {
        withCredentials: true,
      },
    );

    if (response.status !== 200) {
      throw new Error("Failed to update dashboard layout");
    }

    const dashboard = response.data as Dashboard;
    dispatch(conversationActions.upsertDashboard(dashboard));
    dispatch(
      conversationActions.updateDashboardTileLayouts({
        layouts: toSavedDashboardTileLayoutMap(layouts),
      }),
    );

    return dashboard;
  };
};

export const moveDashboardTile = ({
  tileId,
  targetDashboardId,
  targetDashboardTitle,
  sourceExchangeId,
  sourceDashboardId,
}: {
  tileId: string;
  targetDashboardId?: string;
  targetDashboardTitle?: string;
  sourceExchangeId: string;
  sourceDashboardId: string;
}) => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const requestBody: Record<string, string | number> = {};
    if (targetDashboardId) {
      requestBody.dashboard_id = Number(targetDashboardId);
    }
    if (targetDashboardTitle) {
      requestBody.dashboard_title = targetDashboardTitle;
    }

    const response = await axios.patch(
      `/v1/dashboard/tile/${tileId}/move/`,
      requestBody,
      {
        withCredentials: true,
      },
    );

    if (response.status !== 200) {
      throw new Error("Failed to move dashboard tile");
    }

    const dashboard = response.data.dashboard as Dashboard;
    dispatch(conversationActions.upsertDashboard(dashboard));
    dispatch(fetchDashboards() as any);
    dispatch(
      conversationActions.setDashboardExchangeStatus({
        exchangeId: sourceExchangeId,
        status: {
          saved: true,
          loading: false,
          tile_id: tileId,
          dashboard_id: dashboard.id,
          dashboard_title: dashboard.title,
        },
      }),
    );

    const selectedDashboardId = getState().conversationState.selectedDashboard;
    if (
      selectedDashboardId === sourceDashboardId ||
      selectedDashboardId === dashboard.id
    ) {
      await (dispatch(
        fetchDashboardTileIds(selectedDashboardId) as any,
      ) as Promise<void>);
    }

    return response.data;
  };
};

export const deleteDashboardTile = ({
  tileId,
  sourceExchangeId,
  dashboardId,
}: {
  tileId: string;
  sourceExchangeId: string;
  dashboardId: string;
}) => {
  return async (dispatch: Dispatch) => {
    const response = await axios.patch(
      `/v1/dashboard/tile/delete/${tileId}/`,
      {},
      {
        withCredentials: true,
      },
    );

    if (response.status !== 204) {
      throw new Error("Failed to delete dashboard tile");
    }

    dispatch(
      conversationActions.removeDashboardTile({
        tileId,
        dashboardId,
      }),
    );
    dispatch(
      conversationActions.setDashboardExchangeStatus({
        exchangeId: sourceExchangeId,
        status: {
          saved: false,
          loading: false,
          tile_id: undefined,
          dashboard_id: undefined,
          dashboard_title: undefined,
        },
      }),
    );
    dispatch(fetchDashboards() as any);

    return;
  };
};

export const patchDashboard = (id: string, title: string) => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.patch(
        `/v1/dashboard/${id}/`,
        { title },
        {
          withCredentials: true,
        },
      );

      if (response.status !== 200) {
        console.log("Error patching dashboard:", response);
      } else {
        dispatch(
          conversationActions.upsertDashboard(response.data as Dashboard),
        );
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error patching dashboard:", error);
      throw error;
    }
  };
};

export const deleteDashboard = (id: string) => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.patch(
        `/v1/dashboard/delete/${id}/`,
        {},
        {
          withCredentials: true,
        },
      );

      if (response.status !== 204) {
        console.log("Error deleting dashboard");
      } else {
        dispatch(conversationActions.removeDashboard(id));
      }
    };

    try {
      await sendRequest();
    } catch (error) {
      console.error("Error deleting dashboard:", error);
      throw error;
    }
  };
};

export const fetchExchanges = (conversation_id?: string) => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get(
        `/v1/exchange/?conversation_identifier=${conversation_id}`,
        {
          withCredentials: true,
        },
      );
      if (response.status !== 200) {
      } else {
        dispatch(
          conversationActions.loadAllExchanges({
            conversationId: conversation_id,
            exchanges: response.data.objects as Exchange[],
          }),
        );
      }
    };
    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching exchanges:", error);
    }
  };
};

export const fetchMemories = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get("/v1/memory/");
      if (response.status !== 200) {
        console.log("Error fetching memories");
      } else {
        const exchange = {
          response: response.data.objects,
          type: response.data.type,
          id: response.data.id,
        };
        dispatch(conversationActions.appendExchange(exchange as Exchange));
      }
    };
    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching memories:", error);
    }
  };
};

export const fetchSynchronizationStatus = (
  prompt: string,
  temporaryExchangeId: string,
  temporaryConversationId: string,
) => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get<{
        objects: any[];
        type: string;
        conversation_id: string;
        exchange_id: string;
      }>("/v1/integrations/sync_status/");
      if (response.status !== 200) {
        console.log("Error fetching synchronization status");
      } else {
        console.log("Synchronization status:", response.data);
        const syncStatusContent = JSON.stringify(
          response.data.objects[0] ?? {},
        );

        dispatch(
          conversationActions.resolveTemporaryExchangeById({
            temporaryId: temporaryExchangeId,
            temporaryConversationId,
            backendConversationId: response.data.conversation_id,
            backendId: response.data.exchange_id,
            updateFields: {
              id: response.data.exchange_id,
              conversation_id: response.data.conversation_id,
              prompt,
              response: syncStatusContent,
              type: response.data.type,
              json_response: {
                content: syncStatusContent,
                type: response.data.type,
              },
              progress_update: false,
              feedback: null,
              user_comment: null,
            },
          }),
        );
        dispatch(
          conversationActions.setSelectedConversation(
            response.data.conversation_id,
          ),
        );
        dispatch(fetchConversations() as any);
      }
    };
    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching synchronization status:", error);
    }
  };
};

export const fetchAvailableModels = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get("/v1/conversation/available-models/");
      if (response.status !== 200) {
        console.log("Error fetching available models");
      } else {
        dispatch(
          conversationActions.setAvailableModels({
            availableModels: response.data.available_models,
            defaultModel: response.data.default_model,
          }),
        );
      }
    };
    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching exchanges:", error);
    }
  };
};

export default conversationSlice.reducer;

export const fetchSpreadsheets = () => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.get("/v1/google-sheets/");
      if (response.status !== 200) {
        console.log("Error fetching spreadsheets");
      } else {
        const exchange = {
          response: response.data.objects,
          type: response.data.type,
          id: response.data.exchange_id,
        };
        dispatch(conversationActions.appendExchange(exchange as Exchange));
      }
    };
    try {
      await sendRequest();
    } catch (error) {
      console.error("Error fetching spreadsheets:", error);
    }
  };
};
